/**
 * @file useMapData.ts
 * @description
 * React hook responsible for loading and exposing the static map geometry data
 * used by the choropleth visualization.
 *
 * - Fetches polygon topology (congressional district shapes) and point geometry
 *   (optional point markers like district office centroids, etc.).
 * - Converts TopoJSON polygons into GeoJSON FeatureCollections.
 * - Handles asset URL building via the shared `asset()` utility so this works in
 *   both dev (`/`) and production builds where the app may be served off a subpath.
 *
 * This hook does ONLY data I/O / parsing. It does not do any rendering, coloring,
 * or D3 projection logic. That stays in the view layer.
 *
 * @returns {object} An object with `{ polys, points }`, where each is either a
 * GeoJSON FeatureCollection or `null` while still loading.
 *
 * @example
 * const { polys, points } = useMapData();
 * if (!polys) return <Spinner/>;
 * return <PolygonsLayer polys={polys} ... />
 */

import { useEffect, useState } from 'react';
import { feature as topojsonFeature } from 'topojson-client';
import type { Topology, Objects } from 'topojson-specification';
import { asset } from '@/utils/asset';

type GeoFC = GeoJSON.FeatureCollection<GeoJSON.Geometry, Record<string, unknown>>;

export function useMapData() {
  const [polys, setPolys] = useState<GeoFC | null>(null);
  const [points, setPoints] = useState<GeoFC | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        // polygons: congressional districts
        // points: optional centroids/labels/etc.
        const [tRes, pRes] = await Promise.all([
          fetch(asset('data/congress118/polygons.topojson')),
          fetch(asset('data/congress118/points.geojson')),
        ]);

        if (!tRes.ok || !pRes.ok) {
          console.error('[useMapData] fetch failed', {
            polygons: `${tRes.status} ${tRes.statusText}`,
            points: `${pRes.status} ${pRes.statusText}`,
          });
          return;
        }

        // TopoJSON → GeoJSON FeatureCollection
        const topo = (await tRes.json()) as Topology<Objects<GeoJSON.GeoJsonProperties>>;
        const firstObjectKey = Object.keys(topo.objects)[0];
        const firstObject = topo.objects[firstObjectKey];
        if (!firstObject) {
          throw new Error('[useMapData] TopoJSON has no objects');
        }
        const fc = topojsonFeature(topo, firstObject) as GeoFC;

        const pts = (await pRes.json()) as GeoFC;

        if (!cancelled) {
          setPolys(fc);
          setPoints(pts);
        }
      } catch (err) {
        console.error('[useMapData] error', err);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  return { polys, points };
}
